import * as React from "react";
import { useSelector } from "react-redux";
import { de } from "date-fns/locale/de";

import { ApplicationSelectors } from "@com.mgmtp.a12.client/client-core/lib/core/application";
import { NotificationViews } from "@com.mgmtp.a12.client/client-core/lib/core/notification";
import { DirtyHandlingViews } from "@com.mgmtp.a12.client/client-core/lib/extensions/dirtyHandling";
import { AuthenticationState, UaaSelectors } from "@com.mgmtp.a12.uaa/uaa-authentication-client";
import { LocalizerContext } from "@com.mgmtp.a12.utils/utils-localization-react/lib/main";
import {
	defaultDataFormats,
	defaultLocalizerFactory,
	defaultValueConversion
} from "@com.mgmtp.a12.utils/utils-localization/lib/main";
import {
	A11YLanguageContext,
	getA11yResource
} from "@com.mgmtp.a12.widgets/widgets-core/lib/common/main/a11y-localization";
import { SizeContext, useWindowSize } from "@com.mgmtp.a12.widgets/widgets-core/lib/layout/size-detector";
import { DateTimeContext } from "@com.mgmtp.a12.widgets/widgets-core/lib/common/main/date-time/date-time-context";

import { ModulFThemeProvider } from "@de.skhh.modulf/client-core";

import { CustomNotificationRenderer } from "../../component/CustomNofiticationRenderer";
import { CustomProgressIndicator } from "../../component/CustomProgressIndicator";
import { DEFAULT_TRANSLATIONS } from "../../localization";
import { GERMAN_LOCALE } from "../../utilities/localeUtils";

import { AuthenticatedPage } from "./AuthenticatedPage";
import { LogoutPage } from "./LogoutPage";

const RegionWrapper: React.ComponentType = function RegionWrapper() {
	const authenticationState = useSelector(UaaSelectors.state);
	const busyState = useSelector(ApplicationSelectors.busy());

	if (authenticationState === AuthenticationState.NOT_AUTHENTICATED && !busyState) {
		return <LogoutPage />;
	} else if (authenticationState !== AuthenticationState.AUTHENTICATED) {
		// TODO: Fokus landet in diesem Fall nicht immer auf Progress Indicator
		return <CustomProgressIndicator key="global-progress-indicator" global={true} />;
	}

	return (
		<CustomProgressIndicator key="global-progress-indicator" hide={!busyState} global={true}>
			<AuthenticatedPage />
		</CustomProgressIndicator>
	);
};

const FrameWithSizeDetection = function Frame() {
	// Initialize localizations
	const locale = GERMAN_LOCALE;

	const a11yDefinition = React.useMemo(() => getA11yResource(locale.language), [locale.language]);

	const dataFormats = React.useMemo(() => defaultDataFormats(locale), [locale]);
	const conversion = React.useMemo(() => defaultValueConversion(dataFormats), [dataFormats]);

	const localizerContextValue = React.useMemo(() => {
		const dataFormats = defaultDataFormats(locale);
		const localizer = defaultLocalizerFactory({ locale, dataFormats, translationSource: DEFAULT_TRANSLATIONS });
		return { locale, localizer, dataFormats, conversion };
	}, []);

	return (
		<ModulFThemeProvider>
			<LocalizerContext.Provider value={localizerContextValue}>
				<A11YLanguageContext.Provider value={a11yDefinition}>
					<NotificationViews.Frame notificationRenderer={CustomNotificationRenderer}>
						<DirtyHandlingViews.VetoDialog>
							<RegionWrapper />
						</DirtyHandlingViews.VetoDialog>
					</NotificationViews.Frame>
				</A11YLanguageContext.Provider>
			</LocalizerContext.Provider>
		</ModulFThemeProvider>
	);
};

export const ResponsiveFrame: React.ComponentType = function ResponsiveFrame() {
	const { breakPoint } = useWindowSize();

	const size = React.useMemo(() => ({ currentSize: breakPoint.size }), [breakPoint]);

	return (
		<DateTimeContext.Provider value={{ locale: de }}>
			<SizeContext.Provider value={size}>
				<FrameWithSizeDetection />
			</SizeContext.Provider>
		</DateTimeContext.Provider>
	);
};
